import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { Animations } from '@styles/animation/animations';
import { StoryGuideComponent } from '../story-guide/story-guide.component';
import { MascotDialogOrchestratorService } from '../mascot-dialog/mascot-dialog-orchestrator.service';
import { SpotlightRect } from './spotlight-rect.model';

@Component({
  selector: 'recrewt-spotlight-overlay',
  templateUrl: './spotlight-overlay.component.html',
  styleUrl: './spotlight-overlay.component.scss',
  animations: [Animations.fadeIn],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, StoryGuideComponent, A11yModule, TranslateModule],
})
export class SpotlightOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Raw rect to spotlight. Ignored if targetElement is also provided. */
  readonly spotlightRect = input<SpotlightRect | null>(null);

  /**
   * Native DOM element to spotlight. Takes precedence over spotlightRect.
   * The component calls getBoundingClientRect() on AfterViewInit.
   */
  readonly targetElement = input<Element | null>(null);

  /** Translate key or plain string — forwarded to StoryGuideComponent. */
  readonly message = input<string>('');

  /**
   * Controls where the story-guide mascot appears relative to the spotlighted element.
   * Defaults to 'anchored-below'.
   */
  readonly guidePosition = input<'anchored-above' | 'anchored-below'>('anchored-below');

  /** Shares the mascot-dialog priority scale — only one guide overlay is visible at a time. */
  readonly priority = input<number>(0);

  /** Emits when the user clicks anywhere on the overlay. */
  readonly dismiss = output<void>();

  protected isActive$?: Observable<boolean>;

  // The canvas only exists while this overlay is the active one — draw as soon as it appears.
  @ViewChild('overlayCanvas') private set overlayCanvas(
    ref: ElementRef<HTMLCanvasElement> | undefined,
  ) {
    this.canvasRef = ref;
    this.drawOverlay();
  }

  private canvasRef?: ElementRef<HTMLCanvasElement>;

  private readonly destroyRef = inject(DestroyRef);

  private readonly orchestrator = inject(MascotDialogOrchestratorService);

  private claimId?: number;

  ngOnInit(): void {
    this.claimId = this.orchestrator.register(this.priority());
    this.isActive$ = this.orchestrator.isActive$(this.claimId);
  }

  /** Focus returns here when the overlay closes. */
  private readonly previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  ngAfterViewInit(): void {
    this.drawOverlay();
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.drawOverlay());
  }

  ngOnDestroy(): void {
    this.previouslyFocused?.focus();
    if (this.claimId !== undefined) {
      this.orchestrator.unregister(this.claimId);
    }
  }

  private drawOverlay(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const el = this.targetElement();
    const rawRect = this.spotlightRect();

    let x: number, y: number, w: number, h: number, radiusRatio: number;

    if (el) {
      const domRect = el.getBoundingClientRect();
      const padding = 16;
      x = domRect.left - padding;
      y = domRect.top - padding;
      w = domRect.width + padding * 2;
      h = domRect.height + padding * 2;
      radiusRatio = 0.5;
    } else if (rawRect) {
      const padding = rawRect.padding ?? 16;
      x = rawRect.left - padding;
      y = rawRect.top - padding;
      w = rawRect.width + padding * 2;
      h = rawRect.height + padding * 2;
      radiusRatio = rawRect.borderRadius ?? 0.5;
    } else {
      return; // nothing to spotlight — don't draw
    }

    const cornerRadius = Math.min(w, h) * radiusRatio;

    // Fill entire canvas dark
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Punch out the spotlight region
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, cornerRadius);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }
}
